import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import {
  DEFAULT_ADMIN_DEST, LEAD_PARAM, adminDestFromPath, adminRedirectFor, carriedSearch, manageRedirectFor,
  overlayHistory, pathFor, viewFromPath, type AdminDest, type View,
} from '@/shared/routes'

interface CurrentView {
  view: View
  setView: (v: View) => void
  adminDest: AdminDest
  setAdminDest: (d: AdminDest) => void
  /** The lead-centric views share chrome — notes panel, notes toggle, the
   *  leads loading and empty states. Dashboard, Map and Admin are full-bleed
   *  pages that opt out of all of it. */
  leadsView: boolean
  /** Lead whose overlay is open, carried in the query string so a company can
   *  be linked to from any view. */
  overlayLeadId: string | null
  setOverlayLeadId: (id: string | null) => void
}

/**
 * Navigation state, read from the URL rather than mirrored beside it.
 *
 * The previous hand-rolled version kept `mode` in useState and pushed the URL
 * to match with history.replaceState, which meant the URL could only ever
 * describe the current view, never carry anything: replaceState left no
 * history entries so browser Back skipped the whole app, the admin tab was
 * component state so every destination was just /admin, and the open lead was
 * component state so there was no way to send a colleague a link to a company.
 *
 * There is no stored "last view" any more. It only ever applied to landing on
 * a path that named no view, and the URL is now authoritative -- a reload on
 * /table stays on /table because that is what the address bar says, which is
 * the whole point.
 */
export function useCurrentView(): CurrentView {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const matched = viewFromPath(pathname)
  const view = matched ?? 'focus'

  // A path that names no view -- most often /admin/login, where the API drops
  // you after Google sign-in -- is replaced with Focus. replace, not push, so
  // Back does not walk back into it.
  useEffect(() => {
    if (matched === null) navigate(pathFor('focus'), { replace: true })
  }, [matched, navigate])

  // The flat admin paths (/admin/usage, /admin/roles, ...) are bookmarked and
  // linked from inside the app, so each is sent to the section that surface
  // now lives in rather than 404ing or landing on the default tab.
  const legacyAdmin = matched === 'admin' ? adminRedirectFor(pathname) : null
  useEffect(() => {
    if (legacyAdmin) navigate({ pathname: legacyAdmin, search }, { replace: true })
  }, [legacyAdmin, search, navigate])

  // /manage/salesforce lands on Hantera's overview while that section is hidden.
  const bareManage = matched === 'manage' ? manageRedirectFor(pathname) : null
  useEffect(() => {
    if (bareManage) navigate({ pathname: bareManage, search }, { replace: true })
  }, [bareManage, search, navigate])

  const adminDest = view === 'admin' ? adminDestFromPath(pathname) : DEFAULT_ADMIN_DEST

  // The queue being viewed travels with the view switch; nothing else does.
  const setView = useCallback((next: View) => {
    navigate({ pathname: pathFor(next), search: carriedSearch(search) })
  }, [navigate, search])

  const setAdminDest = useCallback((dest: AdminDest) => {
    navigate({ pathname: pathFor('admin', dest), search: carriedSearch(search) })
  }, [navigate, search])

  const overlayLeadId = params.get(LEAD_PARAM)

  const setOverlayLeadId = useCallback((id: string | null) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      if (id) next.set(LEAD_PARAM, id)
      else next.delete(LEAD_PARAM)
      return next
    }, { replace: overlayHistory(overlayLeadId, id) === 'replace' })
  }, [setParams, overlayLeadId])

  return {
    view,
    setView,
    adminDest,
    setAdminDest,
    leadsView: view === 'focus' || view === 'table',
    overlayLeadId,
    setOverlayLeadId,
  }
}
